import type { ViewIds } from '@ardoq/api-types';
import { createWorker } from './getWorker';
import {
  attachLayoutWorkerErrorReporting,
  reportLateLayoutWorkerStart,
  reportLayoutWorkerStartFailure,
} from './layoutWorkerErrorReporting';
import {
  connectLayoutWorker,
  LayoutTarget,
  LayoutWorkerConnection,
  LayoutWorkerStartFailure,
} from './layoutWorkerConnection';
import type { LayoutRequestContext } from './types';

const IDLE_START_TIMEOUT_MS = 10_000;

const state: {
  connection: LayoutWorkerConnection | null;
  lastViewId: ViewIds | null;
  fallbackBeforeAnyLayout: LayoutWorkerStartFailure | null;
} = { connection: null, lastViewId: null, fallbackBeforeAnyLayout: null };

const getRequestContext = (): LayoutRequestContext => ({
  requestedUrl: window.location.href,
  viewId: state.lastViewId,
});

const reportStartFailure = (failure: LayoutWorkerStartFailure): void => {
  if (failure.next === 'newWorker') {
    return;
  }
  const context = getRequestContext();
  if (failure.next === 'mainThread' && context.viewId === null) {
    state.fallbackBeforeAnyLayout = failure;
    return;
  }
  reportLayoutWorkerStartFailure(failure, context);
};

const reportFallbackOnFirstLayout = (): void => {
  const failure = state.fallbackBeforeAnyLayout;
  if (!failure) {
    return;
  }
  state.fallbackBeforeAnyLayout = null;
  reportLayoutWorkerStartFailure(failure, getRequestContext());
};

const ensureConnection = (): LayoutWorkerConnection => {
  if (state.connection) {
    return state.connection;
  }
  state.connection = connectLayoutWorker({
    createWorker,
    onWorkerCreated: attachLayoutWorkerErrorReporting,
    onStartFailure: reportStartFailure,
    onLateStart: lateStart =>
      reportLateLayoutWorkerStart(lateStart, getRequestContext()),
  });
  return state.connection;
};

export const startLayoutWorker = (): void => {
  ensureConnection();
};

export const startLayoutWorkerWhenIdle = (): void => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(startLayoutWorker, { timeout: IDLE_START_TIMEOUT_MS });
    return;
  }
  setTimeout(startLayoutWorker, 0);
};

export const getLayoutWorkerTarget = (
  viewId: ViewIds
): Promise<LayoutTarget> => {
  state.lastViewId = viewId;
  reportFallbackOnFirstLayout();
  return ensureConnection().getTarget();
};
